import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, IdentificationBadge, PencilSimple } from "@phosphor-icons/react";

import { StudentInfoSection } from "@/components/admissions/StudentInfoSection";
import { AddressSection } from "@/components/admissions/AddressSection";
import { ParentSection } from "@/components/admissions/ParentSection";
import { GuardianSection } from "@/components/admissions/GuardianSection";
import { AllocationSection } from "@/components/admissions/AllocationSection";
import { DocumentsPanel } from "@/components/admissions/DocumentsPanel";
import { DraftsTable } from "@/components/admissions/DraftsTable";
import { CsvImportTab } from "@/components/admissions/CsvImportTab";

const initial = {
  first_name: "", middle_name: "", last_name: "",
  roll_number: "",
  dob: "", gender: "male",
  aadhar: "", phone: "", email: "",
  address: "", city: "", state: "", country: "India", pincode: "",
  guardian_name: "", guardian_phone: "",
  guardian_pan: "", guardian_aadhar: "", guardian_address: "",
  parent_father_name: "", parent_mother_name: "",
  is_single_parent: false, single_parent_type: "",
  parent_phone: "", parent_email: "",
  parent_address: "", parent_city: "", parent_state: "", parent_country: "India", parent_pincode: "",
  parent_aadhar: "",
  class_name: "", section: "", room_no: "", bed_no: "",
  hostel_id: "", school_id: "",
  // new fields
  apaar_id: "", academic_session_id: "", admission_type: "fresh", special_requirement: "none",
  discount_pct: null, discount_reason: "", discount_funded_by_donor_id: "",
  is_employee_child: false,
  // Batch 25 — medical + diversity
  height_cm: "", weight_kg: "", blood_group: "",
  category: "general", mother_tongue: "",
  physically_challenged: false, physical_challenge_type: "",
};

const STUDENT_FORM_KEYS = Object.keys(initial);

export default function Admissions() {
  const { user } = useAuth();
  const [form, setForm] = useState(initial);
  const [hostels, setHostels] = useState([]);
  const [schools, setSchools] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [activeStudent, setActiveStudent] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/admin/hostels").then((r) => setHostels(r.data)).catch(() => {});
    api.get("/admin/schools").then((r) => setSchools(r.data)).catch(() => {});
    refreshDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshDrafts = () => {
    api.get("/students").then((r) => setDrafts((r.data || []).filter((s) => s.status === "draft")));
  };

  useEffect(() => {
    if (user?.role === "hostel_admin" && user.hostel_id) setForm((f) => ({ ...f, hostel_id: user.hostel_id }));
    if (user?.role === "school_admin" && user.school_id) setForm((f) => ({ ...f, school_id: user.school_id }));
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.hostel_id && !form.school_id) {
      toast.error("Pick a hostel or school for the admission");
      return;
    }
    if (!form.first_name?.trim()) {
      toast.error("First name is required");
      return;
    }
    if (!form.last_name?.trim()) {
      toast.error("Last name is required");
      return;
    }
    if (!form.class_name?.trim()) {
      toast.error("Class is required");
      return;
    }
    if (!form.blood_group) {
      toast.error("Blood group is required");
      return;
    }
    if (form.is_single_parent && !form.single_parent_type) {
      toast.error("Select which parent is the single guardian (Father or Mother)");
      return;
    }
    if (form.is_single_parent) {
      const need = form.single_parent_type === "mother" ? form.parent_mother_name : form.parent_father_name;
      if (!need?.trim()) {
        toast.error(`Single ${form.single_parent_type}'s name is required`);
        return;
      }
    } else if (!form.parent_father_name?.trim() && !form.parent_mother_name?.trim()) {
      toast.error("At least one parent's name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const res = activeStudent
        ? await api.patch(`/students/${activeStudent.id}`, payload).then(() => api.get(`/students/${activeStudent.id}`))
        : await api.post("/students/admissions", payload);
      const student = res.data;
      toast.success(activeStudent ? "Draft updated" : `Admission draft created — ${student.vs_id}`);
      setActiveStudent(student);
      refreshDrafts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onUploadMedical = async (studentId, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/students/${studentId}/medical-cert`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Medical certificate uploaded — admission ACTIVE");
      refreshDrafts();
      const s = await api.get(`/students/${studentId}`);
      setActiveStudent(s.data);
      return res.data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const onUploadPhoto = async (studentId, kind, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (kind === "student") {
        await api.post(`/students/${studentId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Student photo uploaded");
      } else {
        const docType = kind === "parent" ? "parent_photo" : "guardian_photo";
        fd.append("doc_type", docType);
        fd.append("description", `${kind} photo`);
        await api.post(`/students/${studentId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${kind === "parent" ? "Parent" : "Guardian"} photo uploaded`);
      }
      const s = await api.get(`/students/${studentId}`);
      setActiveStudent(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const onUploadDoc = async (studentId, doc_type, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", doc_type);
      await api.post(`/students/${studentId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${doc_type.replace("_", " ")} uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const startEditDraft = async (d) => {
    const s = await api.get(`/students/${d.id}`);
    setActiveStudent(s.data);
    const next = { ...initial };
    STUDENT_FORM_KEYS.forEach((k) => { if (s.data[k] !== undefined && s.data[k] !== null) next[k] = s.data[k]; });
    next.country = s.data.country || "India";
    next.parent_country = s.data.parent_country || "India";
    setForm(next);
  };

  const resetForm = () => {
    setActiveStudent(null);
    setForm({ ...initial,
      hostel_id: user?.role === "hostel_admin" ? user.hostel_id || "" : "",
      school_id: user?.role === "school_admin" ? user.school_id || "" : "",
    });
  };

  const subtitle = activeStudent
    ? `Editing draft — ${activeStudent.vs_id} (${activeStudent.name})`
    : "Multi-step intake — Draft until medical certificate is uploaded.";

  return (
    <DashboardLayout title="Admissions" subtitle={subtitle}>
      <Tabs defaultValue="new">
        <TabsList>
          <TabsTrigger value="new" data-testid="admissions-tab-new">{activeStudent ? "Update draft" : "New admission"}</TabsTrigger>
          <TabsTrigger value="drafts" data-testid="admissions-tab-drafts">Pending medical gate ({drafts.length})</TabsTrigger>
          <TabsTrigger value="csv" data-testid="admissions-tab-csv">Bulk import (CSV)</TabsTrigger>
        </TabsList>

        <TabsContent value="new">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
            <form onSubmit={onSubmit} className="lg:col-span-2 vs-card p-6 space-y-5" data-testid="admission-form">
              {activeStudent && (
                <div className="flex items-center justify-between p-3 rounded-md border border-[var(--vs-warning)]/30 bg-[var(--vs-warning)]/10 text-sm">
                  <span><PencilSimple size={14} className="inline mr-1.5" /> Updating <strong>{activeStudent.vs_id}</strong> ({activeStudent.name})</span>
                  <button type="button" onClick={resetForm} className="text-xs underline">Start fresh</button>
                </div>
              )}

              <div className="eyebrow">Step 1 — Personal information</div>
              <StudentInfoSection form={form} set={set} idPrefix="adm" requireRequired />

              <div className="vs-divider" />

              <div className="eyebrow">Address</div>
              <AddressSection form={form} set={set} testIdPrefix="adm" requireRequired />

              <div className="vs-divider" />

              <div className="eyebrow"><IdentificationBadge size={12} className="inline mr-1" /> Parent — full information (encrypted at rest)</div>
              <ParentSection form={form} set={set} idPrefix="adm" requireRequired />

              <div className="vs-divider" />

              <div className="eyebrow">Guardian (if different from parent)</div>
              <GuardianSection form={form} set={set} idPrefix="adm" />

              <div className="vs-divider" />

              <div className="eyebrow">Allocation</div>
              <AllocationSection form={form} set={set} hostels={hostels} schools={schools} idPrefix="adm" requireRequired />

              <div className="flex items-center gap-3 pt-3">
                <Button type="submit" disabled={submitting} data-testid="adm-submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
                  {(() => {
                    if (submitting) return activeStudent ? "Updating…" : "Creating draft…";
                    return activeStudent ? "Update draft" : "Create draft";
                  })()}
                </Button>
                <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--vs-secondary)]">
                  <ShieldCheck size={14} weight="fill" /> AES-256 PII at rest
                </div>
              </div>
            </form>

            <DocumentsPanel
              activeStudent={activeStudent}
              uploading={uploading}
              onUploadMedical={onUploadMedical}
              onUploadPhoto={onUploadPhoto}
              onUploadDoc={onUploadDoc}
            />
          </div>
        </TabsContent>

        <TabsContent value="drafts">
          <DraftsTable drafts={drafts} onEdit={startEditDraft} onUploadMedical={onUploadMedical} />
        </TabsContent>

        <TabsContent value="csv">
          <CsvImportTab onImported={refreshDrafts} />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
